<template>
  <v-btn
    :disabled="disabled"
    :color="color"
    :class="`text-${textColor} mb-6`"
    :loading="loading"
    block
    @click="handleClick"
  >
    <template #prepend>
      <v-icon>
        {{ icon }}
      </v-icon>
    </template>
    {{ text }}
  </v-btn>
</template>

<script setup lang="ts">
import { ref, computed, watch, getCurrentInstance, onMounted, onBeforeUnmount, ComponentPublicInstance } from "vue";

import BiometryService from "@/services/BiometryService";
import type Event from "@/types/Event";
import type NominalTicket from "@/types/NominalTicket";
import { useEventsStore } from "@/stores/events";
import { useDialog } from "@/stores/dialog";
import { useSnackbar } from "@/stores/snackbar";

interface Props {
  ticket: NominalTicket;
}

interface VFormInstance {
  register: (item: ComponentPublicInstance | null) => void;
  unregister: (item: ComponentPublicInstance | null) => void;
}

const props = defineProps<Props>();

defineOptions({
  name: "ValidationTicketItemBiometry",
});

const instance = getCurrentInstance();
const dialog = useDialog();
const snackbar = useSnackbar();

// Reactive state
const status = ref<boolean | null>(null);
const loading = ref(false);

// Computed properties
const event = computed<Event | null>(() => useEventsStore().selected);

const hasBiometricAccess = computed((): boolean => {
  if (!event.value) return false;
  if (event.value.access_type === undefined) return false;
  return ["biometric", "hybrid"].includes(event.value.access_type);
});

const text = computed(() => {
  if (status.value === null) return "Preencha os dados";
  if (status.value === false) return "Biometria pendente";
  return "Biometria cadastrada";
});

const disabled = computed(() => status.value === null);

const color = computed(() => {
  if (status.value === null) return "grey-lighten-3";
  if (status.value === false) return "red-darken-3";
  return "green";
});

const icon = computed(() => {
  if (status.value === null) return "mdi-fingerprint-off";
  if (status.value === false) return "mdi-fingerprint-off";
  return "mdi-fingerprint";
});

const textColor = computed(() => {
  if (status.value === null) return "black";
  if (status.value === false) return "white";
  return "white";
});

// Methods
const checkForBiometry = (): void => {
  if (hasBiometricAccess.value === false) return;

  const document = props.ticket.cpf?.replace(/\D/g, "");

  if (!document || document.length < 11) {
    status.value = null;
    return;
  }

  loading.value = true;

  new BiometryService()
    .check(event.value!, document)
    .catch(() => {
      snackbar.show(
        "Erro ao buscar dados biométricos",
        "error",
      );
      status.value = null;
      loading.value = false;
    })
    .then((data) => {
      status.value = data ?? null;
      loading.value = false;
    });
};

const validate = (): boolean => {
  if (!status.value) {
    snackbar.show(
      "É necessário cadastrar a biometria de todos os portadores",
      "error",
    );
  }

  return status.value === true;
};

const handleClick = () => {
  if (status.value !== false) return;

  dialog.show("/biometry/register", {
    params: { ticket: props.ticket },
  });
};

// Watchers
watch(
  () => props.ticket.cpf,
  () => {
    checkForBiometry();
  },
  { immediate: true },
);

// Register with parent v-form (needed because component is not a direct child)
onMounted(() => {
  if (!instance) return;

  let parent = instance.parent;
  while (parent) {
    // Look for v-form in parent chain
    if (parent.type?.name === "VForm") {
      const form = parent.proxy as unknown as VFormInstance;
      if (form.register) {
        form.register(instance.proxy);
      }
      break;
    }
    parent = parent.parent;
  }
});

onBeforeUnmount(() => {
  if (!instance) return;

  let parent = instance.parent;
  while (parent) {
    // Look for v-form in parent chain
    if (parent.type?.name === "VForm") {
      const form = parent.proxy as unknown as VFormInstance;
      if (form.unregister) {
        form.unregister(instance.proxy);
      }
      break;
    }
    parent = parent.parent;
  }
});

// Expose methods to parent component
defineExpose({
  validate,
});
</script>
